'use client';

import { ReactNode } from 'react';
import styled from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';
import EssentialsNutrientsDelivery from 'components/pdp/essentials-nutrients-delivery';
import PropositionCards from 'components/pdp/product-essentials-cards';
import RawHtmlVideo from 'components/pdp/raw-html-video';
import { BodyXlReg, TitleLgReg } from '@seed-health/tokens';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';
import { imageOf } from 'lib/pdp-modules/helpers';
import type {
  EssentialsBundleItem,
  EssentialsModule,
} from 'lib/pdp-modules/types';

const EssentialsWrapper = styled.div`
  background-color: var(--fill-secondary);
`;

const EssentialsGrid = styled.div`
  padding: 1rem 0;
  margin: auto;
  max-width: 1440px;
  display: grid;
  column-gap: 1rem;
  row-gap: 1rem;

  @media ${mqLargeMin} {
    padding: 1rem;
    grid-template-columns: 22.25rem 3fr;
  }
`;

const EssentialsBanner = styled.div`
  position: relative;
  border-radius: 2rem;
  padding: 3rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  color: var(--fill-primary);
  overflow: hidden;

  * {
    color: inherit;
  }

  @media ${mqLargeMin} {
    padding: 1rem;
    padding-top: 6.25rem;
    grid-column: 1 / span 2;
  }
`;

const EssentialsSubtitle = styled.p`
  ${BodyXlReg};
  position: relative;
  margin-top: auto;

  @media ${mqLargeMin} {
    margin-left: 1.5rem;
    font-size: var(--font-size-500);
  }
`;

const EssentialsTitle = styled.h2`
  ${TitleLgReg};
  position: relative;
  margin-top: 10px;
  margin-bottom: 3rem;

  @media ${mqLargeMin} {
    margin-top: 1rem;
    margin-bottom: 0;
    margin-left: 1.5rem;
    text-wrap: auto;
    font-size: var(--font-size-1200);
  }
`;

const EssentialsBannerBackground = styled.img`
  display: block;
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
  object-fit: cover;
  object-position: center left;

  @media ${mqLargeMin} {
    object-position: center 20%;
  }
`;

const EssentialsBannerVideoBackground = styled.div`
  display: block;
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
  object-fit: cover;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const EssentialsOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  inset: 0;
  background-color: black;
  opacity: 0.2;
`;

const BannerTile = styled.div`
  margin: 0 1rem;
  position: relative;
  overflow: hidden;
  border-radius: 2rem;
  order: 3;

  @media ${mqLargeMin} {
    margin: 0;
    order: unset;
  }
`;

const BannerTileVideo = styled.div`
  video {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 100%;
    max-height: 100%;
  }
`;

const BannerTileImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

type ProductEssentialsProps = {
  productInfo: unknown;
  essentials: EssentialsModule;
  bundleItems?: EssentialsBundleItem[];
  selectedSku?: string | false;
  microbiomeModule?: ReactNode;
};

const ProductEssentials = ({
  productInfo,
  essentials,
  bundleItems,
  selectedSku = false,
  microbiomeModule = null,
}: ProductEssentialsProps) => {
  if (!essentials) {
    return null;
  }

  const {
    title,
    subHeader: subheader,
    propositions,
    tiles,
    miniBannerVideoUrl: miniBannerVideoURL,
    miniBannerVideo,
  } = essentials;

  if (!title || !propositions || !tiles?.[0]) {
    return null;
  }

  const mainBannerImage = tiles[0];
  const miniBannerImage = imageOf(tiles[1]);
  const displayMiniBanner =
    miniBannerImage && (miniBannerVideoURL || miniBannerVideo);
  const isSynWelcomeKitSku =
    typeof selectedSku === 'string' &&
    selectedSku === PRODUCT_SKUS.DAILY_SYNBIOTIC;

  return (
    <EssentialsWrapper>
      <EssentialsGrid
        className={`${displayMiniBanner ? 'full-grid' : 'mini-grid'}`}
      >
        <EssentialsBanner>
          <>
            {mainBannerImage.kind === 'video' && (
              <EssentialsBannerVideoBackground>
                <RawHtmlVideo src={mainBannerImage.sources[0].url} />
              </EssentialsBannerVideoBackground>
            )}
            {mainBannerImage.kind !== 'video' && (
              <EssentialsBannerBackground
                src={mainBannerImage?.url}
                alt={mainBannerImage?.alt}
                loading='lazy'
              />
            )}
          </>
          <EssentialsOverlay />
          <EssentialsSubtitle>{subheader}</EssentialsSubtitle>
          <EssentialsTitle>{title}</EssentialsTitle>
          <PropositionCards propositions={propositions} />
        </EssentialsBanner>
        {microbiomeModule}
        {displayMiniBanner && (
          <BannerTile>
            <BannerTileImage
              src={miniBannerImage?.url}
              alt={miniBannerImage?.alt}
              loading='lazy'
            />
            <BannerTileVideo>
              <RawHtmlVideo
                src={miniBannerVideoURL}
                backupSrc={
                  miniBannerVideo?.kind === 'video'
                    ? miniBannerVideo.sources[0]?.url
                    : undefined
                }
              />
            </BannerTileVideo>
          </BannerTile>
        )}
        <EssentialsNutrientsDelivery
          productInfo={productInfo}
          essentials={essentials}
          bundleItems={bundleItems}
          isDSVariant={isSynWelcomeKitSku}
        />
      </EssentialsGrid>
    </EssentialsWrapper>
  );
};

export default ProductEssentials;
